// Layer 3 (Transformation): format Case.affects as a one-line KPI lead read.

import type { Case } from '@/versions/v1/schema/case';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { formatValue, readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { seriesAt, readingsAt } from '@/versions/v1/shared/series.derive';
import { SERIES_BY_READING_ID } from '@/versions/v1/shared/bunker-hill-series';
import { bm01BearingTrend } from '@/versions/v1/modules/downtime/ValueLost.transform';

function leadLabel(hours: number): string {
  if (hours >= 24 * 7) {
    const weeks = Math.round(hours / (24 * 7));
    return `~${weeks}\u00a0wk`;
  }
  if (hours >= 24) {
    const days = Math.round(hours / 24);
    return `~${days}\u00a0d`;
  }
  return `~${hours}\u00a0h`;
}

function assetLabel(caseItem: Case): string {
  if (caseItem.circuit.includes('Ball mill')) return 'Ball mill trunnion bearing';
  return caseItem.title.split('—')[0]?.trim() ?? caseItem.circuit;
}

function affectsValueText(caseItem: Case, state: SessionState): string | undefined {
  const affects = caseItem.affects;
  if (!affects) return undefined;
  const reading = readingByIdLookup(affects.readingId);
  const seriesId = SERIES_BY_READING_ID[affects.readingId]?.id;
  if (seriesId) {
    const latest = seriesAt(seriesId, state).latest?.value;
    if (latest !== undefined && reading) {
      return formatValue(reading, { value: latest, withUnit: true });
    }
  }
  if (reading) {
    const projected = readingsAt(state).find((r) => r.id === affects.readingId);
    const val = projected?.value ?? reading.value;
    if (val !== undefined) {
      return formatValue(reading, { value: val, withUnit: true });
    }
  }
  return undefined;
}

/** The two parts of the affects read a figure cell sets apart: the KPI the
 *  case reaches and the lead before it does (`Mill utilisation`, `~6 d`).
 *  The same lead `formatAffectsLine` prints, read once. */
export function caseAffectsRead(
  caseItem: Case,
  state: SessionState,
): { kpiLabel: string; lead: string } | undefined {
  const affects = caseItem.affects;
  if (!affects) return undefined;
  const kpiLabel = readingByIdLookup(affects.readingId)?.label ?? affects.readingId;
  let leadHours = affects.leadHours;
  if (affects.leadSource === 'bearing-limit-trend') {
    // Before the post-restart trend can be fitted the horizon is unmeasured, so
    // the lead is absent rather than standing on a stale typed value (#782).
    const trend = bm01BearingTrend(state);
    const daysToLimit = trend.state === 'fitted' ? trend.daysToLimit : null;
    leadHours = daysToLimit === null ? undefined : daysToLimit * 24;
  }
  if (leadHours === undefined) return undefined;
  return { kpiLabel, lead: leadLabel(leadHours) };
}

export function formatAffectsLine(caseItem: Case, state: SessionState): string | undefined {
  const affects = caseItem.affects;
  if (!affects) return undefined;
  const reading = readingByIdLookup(affects.readingId);
  const kpiLabel = reading?.label ?? affects.readingId;
  const valueText = caseItem.headline?.value ?? affectsValueText(caseItem, state);
  let leadHours = affects.leadHours;
  if (affects.leadSource === 'bearing-limit-trend') {
    const trend = bm01BearingTrend(state);
    const daysToLimit = trend.state === 'fitted' ? trend.daysToLimit : null;
    leadHours = daysToLimit === null ? undefined : daysToLimit * 24;
  }
  if (!valueText || leadHours === undefined) return undefined;
  return `${assetLabel(caseItem)} ${valueText} → ${kpiLabel} in ${leadLabel(leadHours)}`;
}
